import type { ChangeEvent } from 'react';
import { useCallback, useId, useMemo } from 'react';

import { Box, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';

import { useExportSettings } from '@/features/DataHub/modules/Export/context/exportContexts';
import {
  DateRangeType,
  type ExportSettingsType
} from '@/features/DataHub/modules/Export/types/context.types';
import {
  getDateRangeValidation,
  sanitizePeriodNumber
} from '@/features/DataHub/modules/Export/utils';

import { DateRangePickers } from './DateRangePickers';
import { RangePeriodFields } from './RangePeriodFields';

import type { SelectChangeEvent } from '@mui/material/Select';

type DateRangeSettingsFieldsProps = {
  dateFormat: string;
  minDate: Date;
  maxDate: Date;
};

const DateRangeSettingsFields = ({
  dateFormat,
  minDate,
  maxDate
}: DateRangeSettingsFieldsProps) => {
  const radioGroupId = useId();

  const { exportSettings, updateExportSetting } = useExportSettings();
  const { dateRangeType, from, to, period, periodNumber } = exportSettings;

  const onChangeDateRangeType = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      updateExportSetting('dateRangeType', e.target.value as DateRangeType);
    },
    [updateExportSetting]
  );

  const onChangePeriodNumber = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      updateExportSetting('periodNumber', sanitizePeriodNumber(e.target.value));
    },
    [updateExportSetting]
  );

  const onChangePeriod = useCallback(
    (e: SelectChangeEvent<ExportSettingsType['period']>) => {
      updateExportSetting('period', e.target.value);
    },
    [updateExportSetting]
  );

  const onChangeDateRange = useCallback(
    (field: 'from' | 'to', value: Date | null) => {
      updateExportSetting(field, value);
    },
    [updateExportSetting]
  );

  const validation = useMemo(
    () =>
      getDateRangeValidation({
        dateRangeType,
        from,
        to,
        periodNumber
      }),
    [dateRangeType, from, to, periodNumber]
  );

  const isDatePickerDisabled = dateRangeType !== DateRangeType.DATES;

  return (
    <FormControl>
      <RadioGroup
        aria-labelledby={radioGroupId}
        value={dateRangeType}
        onChange={onChangeDateRangeType}
      >
        <FormControlLabel
          control={<Radio />}
          label='All'
          value={DateRangeType.ALL}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 50,
            marginBottom: validation.periodNumberError ? 2 : 0
          }}
        >
          <FormControlLabel
            control={<Radio />}
            label='Last'
            value={DateRangeType.RANGE}
          />
          <RangePeriodFields
            period={period}
            periodNumber={periodNumber}
            periodNumberError={validation.periodNumberError}
            onChangePeriod={onChangePeriod}
            onChangePeriodNumber={onChangePeriodNumber}
          />
        </Box>
        <FormControlLabel
          control={<Radio />}
          label='From'
          value={DateRangeType.DATES}
        />
        <DateRangePickers
          dateFormat={dateFormat}
          from={from}
          fromError={validation.fromError}
          fromErrorMessage={validation.fromErrorMessage}
          isDatePickerDisabled={isDatePickerDisabled}
          maxDate={maxDate}
          minDate={minDate}
          to={to}
          toError={validation.toError}
          toErrorMessage={validation.toErrorMessage}
          onChange={onChangeDateRange}
        />
      </RadioGroup>
    </FormControl>
  );
};

export default DateRangeSettingsFields;
